import { CONFERENCE_JOINED, CONFERENCE_LEFT, CONFERENCE_WILL_JOIN } from '../base/conference/actionTypes';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { TRACK_ADDED, TRACK_CREATE_ERROR, TRACK_MUTE_UNMUTE_FAILED, TRACK_REMOVED, TRACK_UPDATED } from '../base/tracks/actionTypes';
import { shortFingerprint } from '../base/util/shortFingerprint';
import { emitMobileTelemetryEvent } from '../mobile/navigation/telemetry';
import { getPersistentJson } from '../vrs-auth/storage';
import { getConferenceRoomName } from './callLifecycleGuards';
/**
 * Which physical camera a local video track holds: its model label and a
 * fingerprint of the browser's (already per-site) device id. With two
 * browsers on one machine, this is how telemetry shows which one holds a
 * camera the other cannot open (2026-09-23). No account data.
 */
function cameraIdentity(track) {
    const jitsiTrack = track?.jitsiTrack;
    if (trackMediaType(track) !== 'video' || track?.videoType === 'desktop' || !jitsiTrack) {
        return {};
    }
    const label = typeof jitsiTrack.getTrackLabel === 'function' ? String(jitsiTrack.getTrackLabel() || '') : '';
    const deviceId = typeof jitsiTrack.getDeviceId === 'function' ? String(jitsiTrack.getDeviceId() || '') : '';
    return {
        cameraLabel: label ? label.slice(0, 60) : undefined,
        cameraRef: deviceId ? shortFingerprint(deviceId) : undefined
    };
}
/**
 * The camera a local track streams after it was turned back on in place, and
 * whether it is the camera the track reports. The lib-jitsi-meet unmute
 * reopens a camera by a bare (ideal) id and never updates getDeviceId(), so a
 * browser that opened another camera shows up only here (matchedRequest
 * false). The web reopens stopped cameras itself (setLocalTrackMuted, which
 * reports through camera_device_mismatch and a new track); this covers any
 * unmute left to the lib.
 *
 * @param {TrackLike} track - The track from a TRACK_UPDATED action.
 * @returns {Object} Camera fields for media_local_track_muted_changed.
 */
function unmutedCamera(track) {
    const jitsiTrack = track?.jitsiTrack;
    if (trackMediaType(track) !== 'video' || jitsiTrack?.getVideoType?.() === 'desktop') {
        return {};
    }
    const streaming = jitsiTrack?.getTrack?.()?.getSettings?.()?.deviceId;
    const reported = jitsiTrack?.getDeviceId?.();
    return {
        ...cameraIdentity(track),
        matchedRequest: streaming && reported ? streaming === reported : undefined,
        obtainedCameraRef: streaming ? shortFingerprint(streaming) : undefined
    };
}
function isLocalTrack(track) {
    return Boolean(track?.local === true || track?.jitsiTrack?.isLocal?.());
}
function trackMediaType(track) {
    if (typeof track?.mediaType === 'string') {
        return track.mediaType;
    }
    // Current actions wrap the SDK track; retain support for legacy raw tracks.
    const type = track?.jitsiTrack?.getType?.() ?? track?.getType?.();
    return typeof type === 'string' ? type : undefined;
}
/**
 * Emits privacy-safe, bounded telemetry for the local media lifecycle.
 *
 * The 2026-09-06 interpreter outbound-media incident was only diagnosable to
 * the granularity of `media_quality_sample` (upload/download bitrate sampled
 * every 30s) — enough to see the interpreter was not publishing, but not
 * enough to say whether the camera was ever acquired, whether the track was
 * created, or whether a mute/unmute or device-permission failure aborted the
 * publication. These events close that gap by reporting the meaningful local
 * media transitions as they happen, without raw media, device identifiers, or
 * any other PII. Publication success/failure at the RTP level remains visible
 * through the existing `media_quality_sample` `bitrateUploadKbps`.
 */
MiddlewareRegistry.register((store) => {
    let timing;
    let readyTracks = new WeakSet();
    return next => (action) => {
        const result = next(action);
        if (action.type === CONFERENCE_WILL_JOIN && action.conference) {
            timing = { conference: action.conference, startedAt: Date.now() };
            readyTracks = new WeakSet();
        }
        else if (action.type === CONFERENCE_JOINED && timing && timing.conference === action.conference) {
            timing.joinedAt ?? (timing.joinedAt = Date.now());
        }
        else if (action.type === CONFERENCE_LEFT && timing && timing.conference === action.conference) {
            timing = undefined;
        }
        if (action.type === TRACK_UPDATED && action.track?.videoStarted === true && timing) {
            const state = store.getState();
            const conferenceState = state['features/base/conference'];
            const currentConference = conferenceState.joining ?? conferenceState.conference;
            const track = state['features/base/tracks'].find(candidate => candidate.jitsiTrack === action.track?.jitsiTrack);
            const sdkTrack = track?.jitsiTrack;
            if (currentConference === timing.conference && sdkTrack && track
                && (track.local || sdkTrack.conference === currentConference)
                && track.mediaType === 'video' && !readyTracks.has(sdkTrack)) {
                readyTracks.add(sdkTrack);
                const roomName = getConferenceRoomName(currentConference);
                const stored = getPersistentJson('vrs_active_call');
                const call = stored && typeof stored === 'object' ? stored : undefined;
                const options = { roomName };
                if (roomName && call?.roomName === roomName) {
                    options.callId = typeof call.callId === 'string' ? call.callId : undefined;
                    options.requestId = typeof call.requestId === 'string' ? call.requestId : undefined;
                }
                const now = Date.now();
                // Client wall-clock intervals, consistent with existing queue/join
                // timings. Server receipt times must not be used as media timing.
                // RTCView dimensions are weaker evidence than web onplaying; neither
                // establishes audible audio or physical-device acceptance.
                emitMobileTelemetryEvent(typeof navigator !== 'undefined' && navigator.product === 'ReactNative'
                    ? 'media_video_dimensions_available' : 'media_video_playing', {
                    joinToVideoMs: timing.joinedAt === undefined ? undefined : Math.max(0, now - timing.joinedAt),
                    local: track.local,
                    mediaType: track.mediaType,
                    videoType: track.videoType,
                    willJoinToVideoMs: Math.max(0, now - timing.startedAt)
                }, options);
            }
        }
        switch (action.type) {
            case TRACK_ADDED:
                if (isLocalTrack(action.track)) {
                    emitMobileTelemetryEvent('media_local_track_added', {
                        local: true,
                        mediaType: trackMediaType(action.track),
                        muted: action.track?.muted,
                        videoType: action.track?.videoType,
                        ...cameraIdentity(action.track)
                    });
                }
                break;
            case TRACK_REMOVED:
                // Pairs with media_local_track_added: a camera added without a
                // matching removal is still held by this page.
                if (isLocalTrack(action.track) && trackMediaType(action.track) === 'video') {
                    emitMobileTelemetryEvent('media_local_track_removed', {
                        mediaType: 'video',
                        videoType: action.track?.videoType,
                        ...cameraIdentity(action.track)
                    });
                }
                break;
            case TRACK_UPDATED:
                // TRACK_UPDATED is also used for videoType and no-data notifications;
                // only treat a payload that carries a boolean `muted` as a mute change.
                if (isLocalTrack(action.track) && typeof action.track?.muted === 'boolean') {
                    emitMobileTelemetryEvent('media_local_track_muted_changed', {
                        mediaType: trackMediaType(action.track),
                        muted: action.track.muted,
                        ...action.track.muted ? {} : unmutedCamera(action.track)
                    });
                }
                break;
            case TRACK_MUTE_UNMUTE_FAILED:
                emitMobileTelemetryEvent('media_track_mute_failed', {
                    mediaType: trackMediaType(action.track),
                    wasMuting: action.wasMuting
                });
                break;
            case TRACK_CREATE_ERROR:
                // getUserMedia failure: covers both denied permission and device
                // acquisition failure without exposing device identifiers.
                emitMobileTelemetryEvent('media_track_create_failed', {
                    permissionDenied: action.permissionDenied,
                    trackType: action.trackType
                });
                break;
        }
        return result;
    };
});
